import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

type NavItemType = 'section' | 'page';

interface NavItem {
  key: string;
  section: string;
  type: NavItemType;
  path?: string;
}

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  const isHome = location.pathname === '/';

  const navItems: NavItem[] = [
    { key: 'nav.home', section: 'home', type: 'section' },
    { key: 'nav.products', section: 'products', type: 'page', path: '/products' },
    { key: 'nav.scenarios', section: 'portfolio', type: 'section' },
    { key: 'nav.about', section: 'about', type: 'section' },
    { key: 'nav.contact', section: 'contact', type: 'section' },
  ];

  const sectionItems = navItems.filter(n => n.type === 'section');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      if (!isHome) return;
      for (let i = sectionItems.length - 1; i >= 0; i--) {
        const el = document.getElementById(sectionItems[i].section);
        if (el && window.scrollY >= el.offsetTop - 100) {
          setActiveSection(sectionItems[i].section);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHome]);

  const handleNavClick = (item: NavItem) => {
    setMenuOpen(false);
    if (item.type === 'page') {
      if (isOnProducts) {
        // Already on products page — scroll back to Hero top
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigate(item.path!);
      }
      return;
    }
    if (isHome) {
      const el = document.getElementById(item.section);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(`/#${item.section}`);
    }
  };

  const toggleLang = () => {
    const next = i18n.language === 'zh' ? 'en' : 'zh';
    i18n.changeLanguage(next);
  };

  const isZh = i18n.language === 'zh';
  const isOnProducts = location.pathname === '/products';

  const isActive = (item: NavItem) => {
    if (item.type === 'page') return isOnProducts;
    return activeSection === item.section && isHome;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || isOnProducts ? 'bg-white shadow-sm py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <h1 className="flex items-center">
          <button onClick={() => navigate('/')} className="cursor-pointer flex items-center">
            <img
              src="https://storage.readdy-site.link/project_files/f2e80cb9-6e9d-476e-b707-141660fae558/c191956b-eb38-4728-964c-f61cd28745ef_logo.png?v=ee2af49cd0d2ab77aa07359f00783dce"
              alt="ONOPTO Logo"
              className="h-9 w-auto object-contain align-middle"
            />
          </button>
        </h1>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-7">
          {navItems.map(item => (
            <button
              key={item.key}
              onClick={() => handleNavClick(item)}
              className={`text-base font-medium tracking-wide transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                isActive(item)
                  ? (scrolled || isOnProducts ? 'text-[#1B3F7A]' : 'text-white')
                  : (scrolled || isOnProducts)
                  ? 'text-gray-600 hover:text-[#1B3F7A]'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              {t(item.key)}
            </button>
          ))}

          {/* Language Toggle */}
          <button
            onClick={toggleLang}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
              scrolled || isOnProducts
                ? 'border-gray-300 text-gray-600 hover:border-[#1B3F7A] hover:text-[#1B3F7A]'
                : 'border-white/40 text-white/80 hover:text-white hover:border-white'
            }`}
          >
            <i className="ri-translate-2 text-sm" />
            {isZh ? 'EN' : '中文'}
          </button>

          <button
            onClick={() => {
              if (isOnProducts) {
                const el = document.getElementById('cta');
                if (el) {
                  const top = el.getBoundingClientRect().top + window.scrollY - 80;
                  window.scrollTo({ top, behavior: 'smooth' });
                }
              } else {
                sessionStorage.setItem('scrollToCTA', '1');
                navigate('/products');
              }
            }}
            className="ml-1 px-5 py-2 rounded-full text-base font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap bg-[#1B3F7A] text-white hover:bg-[#152f5e]"
          >
            {t('nav.cta')}
          </button>
        </nav>

        {/* Mobile right actions */}
        <div className="md:hidden flex items-center gap-3">
          <button
            onClick={toggleLang}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full border text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              scrolled || isOnProducts ? 'border-gray-300 text-gray-600' : 'border-white/40 text-white/80'
            }`}
          >
            <i className="ri-translate-2 text-sm" />
            {isZh ? 'EN' : '中文'}
          </button>
          <button
            className="w-8 h-8 flex items-center justify-center cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'} ${scrolled || isOnProducts ? 'text-gray-800' : 'text-white'}`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-4">
          {navItems.map(item => (
            <button
              key={item.key}
              onClick={() => handleNavClick(item)}
              className="text-left text-sm font-medium text-gray-700 hover:text-[#1B3F7A] transition-colors cursor-pointer"
            >
              {t(item.key)}
            </button>
          ))}
          <button
            onClick={() => {
              setMenuOpen(false);
              if (isOnProducts) {
                const el = document.getElementById('cta');
                if (el) {
                  const top = el.getBoundingClientRect().top + window.scrollY - 80;
                  window.scrollTo({ top, behavior: 'smooth' });
                }
              } else {
                sessionStorage.setItem('scrollToCTA', '1');
                navigate('/products');
              }
            }}
            className="mt-2 px-5 py-2 rounded-full text-sm font-semibold bg-[#1B3F7A] text-white hover:bg-[#152f5e] transition-all cursor-pointer whitespace-nowrap w-fit"
          >
            {t('nav.cta')}
          </button>
        </div>
      )}
    </header>
  );
}
